/**
 * placesService.js - Servicio frontend para el directorio de lugares
 *
 * Comunicación con /api/directory/places y /api/directory/google
 */
import { API_URL } from '../../services/api/config';
import { getAuthHeaders, fetchWrapper } from '../../services/api/utils/fetchWrapper';

const BASE_URL = `${API_URL}/api/directory/places`;
const GOOGLE_URL = `${API_URL}/api/directory/google`;

/**
 * Obtener lista de lugares con filtros
 * @param {Object} filtros - { categoria, tipo, status, ciudad, busqueda, limite, contactoStatus, lat, lng, noFallback }
 * @returns {Promise<Array>} Lista de lugares
 */
export async function getPlaces(filtros = {}) {
  const params = new URLSearchParams();

  if (filtros.categoria) params.append('categoria', filtros.categoria);
  if (filtros.tipo) params.append('tipo', filtros.tipo);
  if (filtros.status) params.append('status', filtros.status);
  if (filtros.ciudad) params.append('ciudad', filtros.ciudad);
  if (filtros.busqueda) params.append('busqueda', filtros.busqueda);
  if (filtros.limite) params.append('limite', filtros.limite);
  if (filtros.contactoStatus) params.append('contactoStatus', filtros.contactoStatus);
  if (filtros.lat) params.append('lat', filtros.lat);
  if (filtros.lng) params.append('lng', filtros.lng);
  if (filtros.noFallback) params.append('noFallback', 'true');

  const queryString = params.toString();
  const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL;

  console.log('[placesService] getPlaces URL:', url);

  const headers = await getAuthHeaders();
  const response = await fetchWrapper(url, { headers });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al obtener lugares');
  }

  const data = await response.json();

  // Log para debug
  const propios = (data.data || []).filter(l => l.source !== 'google_cache').length;
  const google = (data.data || []).filter(l => l.source === 'google_cache').length;
  console.log(`[placesService] Resultados: ${data.data?.length || 0} total (propios: ${propios}, google: ${google})`);

  return data.data || [];
}

/**
 * Obtener detalle de un lugar
 * @param {string} id - ID del lugar
 * @returns {Promise<Object>} Lugar con info de favorito
 */
export async function getPlace(id) {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${BASE_URL}/${id}`, { headers });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Lugar no encontrado');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Crear un nuevo lugar
 * @param {Object} data - Datos del lugar
 * @returns {Promise<Object>} Lugar creado
 */
export async function createPlace(data) {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(BASE_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al crear lugar');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Actualizar un lugar (solo admin)
 * @param {string} id - ID del lugar
 * @param {Object} data - Datos a actualizar
 * @returns {Promise<Object>} Lugar actualizado
 */
export async function updatePlace(id, data) {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${BASE_URL}/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al actualizar lugar');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Eliminar un lugar (solo admin)
 * @param {string} id - ID del lugar
 * @returns {Promise<boolean>} true si se eliminó
 */
export async function deletePlace(id) {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${BASE_URL}/${id}`, {
    method: 'DELETE',
    headers
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al eliminar lugar');
  }

  return true;
}

/**
 * Verificar un lugar (solo admin)
 * @param {string} id - ID del lugar
 * @returns {Promise<Object>} Lugar verificado
 */
export async function verifyPlace(id) {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${BASE_URL}/${id}/verify`, {
    method: 'POST',
    headers
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al verificar lugar');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Obtener estadísticas del directorio (solo admin)
 * @returns {Promise<Object>} Estadísticas
 */
export async function getPlacesStats() {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${BASE_URL}/stats`, { headers });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al obtener estadísticas');
  }

  const data = await response.json();
  return data.data;
}

// ================== SEGUIMIENTO (CRM) ==================

/**
 * Actualizar seguimiento de un lugar (solo admin)
 * @param {string} id - ID del lugar
 * @param {Object} data - { status, asignadoA, asignadoNombre, proximoContacto, proximoContactoNota }
 * @returns {Promise<Object>} Lugar actualizado
 */
export async function updateSeguimiento(id, data) {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${BASE_URL}/${id}/seguimiento`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al actualizar seguimiento');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Agregar interacción al historial (solo admin)
 * @param {string} placeId - ID del lugar
 * @param {Object} data - { tipo, fecha, notas }
 * @returns {Promise<Object>} Lugar actualizado
 */
export async function addInteraccion(placeId, data) {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${BASE_URL}/${placeId}/interaccion`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al agregar interacción');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Eliminar interacción del historial (solo admin)
 * @param {string} placeId - ID del lugar
 * @param {string} interaccionId - ID de la interacción
 * @returns {Promise<Object>} Lugar actualizado
 */
export async function deleteInteraccion(placeId, interaccionId) {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${BASE_URL}/${placeId}/interaccion/${interaccionId}`, {
    method: 'DELETE',
    headers
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al eliminar interacción');
  }

  const result = await response.json();
  return result.data;
}

// ================== GOOGLE PLACES LINKING ==================

/**
 * Buscar en Google Places para vincular (solo admin)
 * @param {string} nombre - Nombre del lugar
 * @param {string} direccion - Dirección (opcional)
 * @param {string} ciudad - Ciudad (opcional)
 * @returns {Promise<Array>} Coincidencias de Google
 */
export async function searchGooglePlaces(nombre, direccion = '', ciudad = '') {
  const params = new URLSearchParams();
  params.append('nombre', nombre);
  if (direccion) params.append('direccion', direccion);
  if (ciudad) params.append('ciudad', ciudad);

  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${GOOGLE_URL}/search?${params.toString()}`, { headers });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al buscar en Google');
  }

  const data = await response.json();
  return data.data || [];
}

/**
 * Vincular lugar con Google Places (solo admin)
 * @param {string} placeId - ID del lugar
 * @param {string} googlePlaceId - ID de Google Places
 * @param {number} rating - Rating de Google
 * @param {number} reviewCount - Número de reseñas
 * @returns {Promise<Object>} Lugar actualizado
 */
export async function vincularConGoogle(placeId, googlePlaceId, rating = null, reviewCount = null) {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${BASE_URL}/${placeId}/vincular-google`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ googlePlaceId, rating, reviewCount })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al vincular con Google');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Desvincular lugar de Google Places (solo admin)
 * @param {string} placeId - ID del lugar
 * @returns {Promise<Object>} Lugar actualizado
 */
export async function desvincularGoogle(placeId) {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${BASE_URL}/${placeId}/vincular-google`, {
    method: 'DELETE',
    headers
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al desvincular de Google');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Verificar estado de Google Places API
 * @returns {Promise<Object>} { configured, message }
 */
export async function getGoogleStatus() {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${GOOGLE_URL}/status`, { headers });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al verificar estado de Google');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Hacer test de búsqueda en Google Places API
 * @param {string} categoria - Categoría a buscar (default: Veterinaria)
 * @param {string} ciudad - Ciudad a buscar (default: Ciudad de México)
 * @returns {Promise<Object>} { configured, testQuery, resultCount, durationMs, results }
 */
export async function testGooglePlaces(categoria = 'Veterinaria', ciudad = 'Ciudad de México') {
  const params = new URLSearchParams();
  params.append('categoria', categoria);
  params.append('ciudad', ciudad);

  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${GOOGLE_URL}/test?${params.toString()}`, { headers });

  const data = await response.json();

  if (!response.ok) {
    console.error('[placesService] Google test failed:', data);
    throw new Error(data.error || 'Error al hacer test de Google');
  }

  console.log('[placesService] Google test result:', data.data);
  return data.data;
}

/**
 * Limpiar cache expirado de Google (solo admin)
 * @returns {Promise<Object>} { deleted, message }
 */
export async function cleanupGoogleCache() {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${GOOGLE_URL}/cleanup`, {
    method: 'POST',
    headers
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al limpiar cache');
  }

  const data = await response.json();
  return data.data;
}

export default {
  getPlaces,
  getPlace,
  createPlace,
  updatePlace,
  deletePlace,
  verifyPlace,
  getPlacesStats,
  updateSeguimiento,
  addInteraccion,
  deleteInteraccion,
  // Google Places
  searchGooglePlaces,
  vincularConGoogle,
  desvincularGoogle,
  getGoogleStatus,
  testGooglePlaces,
  cleanupGoogleCache
};
